import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { sizedImageUrl } from '@/utils/sizedImageUrl';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, BookOpen, Check, ChevronRight, GraduationCap, Globe, KeyRound, Lock, Mail, Moon, Quote, Sun, Target, Users } from 'lucide-react';
import type { RegistrationField } from '@/services/registration-form.service';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { Checkbox } from '@/components/ui/Checkbox/Checkbox';
import { DropdownMenu } from '@/components/ui/DropdownMenu/DropdownMenu';
import { useLoginPage } from '@/hooks/shared/useLoginPage';
import { MfaChallenge } from '@/components/auth/MfaChallenge/MfaChallenge';
import { Tr, tr, useLanguage, appLocale } from '@/i18n';
import { localizeAuthMessage } from '@/auth/authMessages';
import { useInstalledLanguages } from '@/hooks/useInstalledLanguages';
import { parseMultilang } from '@/utils/multilang';
import { sanitizeHtml } from '@/utils/sanitize';

import styles from './LoginPage.module.css';

import logoIconDark from '../../../public/assets/logos/logo-white.svg';
import logoIconLight from '../../../public/assets/logos/logo-full.svg';

import eyeClosedGrey from '../../../public/assets/icons/eye-closed-grey.svg';
import eyeOpenGrey from '../../../public/assets/icons/eye-open-grey.svg';
import eyeClosedWhite from '../../../public/assets/icons/eye-closed-white.svg';
import eyeOpenWhite from '../../../public/assets/icons/eye-open-white.svg';
import { API_PREFIX } from '@/api/endpoints/api.constants';
import { applyTenantAccent } from '@/utils/tenantAccent';
import { useTheme, ownSignedOutTheme } from '@/contexts/shared/ThemeContext';

// Public (pre-auth) calls must name the tenant explicitly. Without the header
// the backend resolves the tenant from the API host's subdomain, which may map
// to a different tenant than the one the user is looking at (prod: 'ilearn'
// resolved to the Test tenant → wrong branding / no login image).
const publicHeaders = (): Record<string, string> => ({
    'X-Tenant-ID': localStorage.getItem('tenant_id') || '1',
});

// Note: there is intentionally NO default hero photo — until the tenant's
// login image loads, the hero shows a gradient (see .heroSection CSS) so we
// never flash a placeholder photo before branding resolves.

// Phone dial codes for the sign-up mobile field. Country names come from
// Intl.DisplayNames in the interface language (no hardcoded English names).
const DIAL_CODES: ReadonlyArray<{ code: string; region: string }> = [
    { code: '+971', region: 'AE' }, { code: '+966', region: 'SA' },
    { code: '+973', region: 'BH' }, { code: '+974', region: 'QA' },
    { code: '+968', region: 'OM' }, { code: '+965', region: 'KW' },
    { code: '+962', region: 'JO' }, { code: '+961', region: 'LB' },
    { code: '+20', region: 'EG' }, { code: '+212', region: 'MA' },
    { code: '+91', region: 'IN' }, { code: '+92', region: 'PK' },
    { code: '+63', region: 'PH' }, { code: '+44', region: 'GB' },
    { code: '+1', region: 'US' }, { code: '+33', region: 'FR' },
    { code: '+49', region: 'DE' }, { code: '+90', region: 'TR' },
    { code: '+86', region: 'CN' }, { code: '+81', region: 'JP' },
];

// Labels for the platform's standard sign-up fields, used when the
// registration form doesn't define a label in the visitor's language
// (built at call time — tr() strings aren't loaded at module evaluation).
const coreFieldLabel = (fieldKey: string): string | null => {
    switch (fieldKey.toLowerCase()) {
        case 'username': return tr('login.signup_field_username', 'Username');
        case 'firstname': case 'first_name': return tr('login.field_firstname', 'First name');
        case 'lastname': case 'last_name': return tr('login.field_lastname', 'Last name');
        case 'email': return tr('ui.email_address_b357b5', 'Email address');
        case 'password': return tr('login.field_password', 'Password');
        case 'mobile': case 'phone': return tr('ui.mobile_87d17f', 'Mobile');
        case 'dob': case 'date_of_birth': return tr('login.signup_field_dob', 'Date of birth');
        case 'gender': return tr('login.signup_field_gender', 'Gender');
        default: return null;
    }
};

// Standard gender options, translated when the stored option label is plain text.
const coreOptionLabel = (fieldKey: string, value: string): string | null => {
    if (fieldKey.toLowerCase() !== 'gender') return null;
    if (value === 'male') return tr('login.gender_male', 'Male');
    if (value === 'female') return tr('login.gender_female', 'Female');
    return null;
};

interface PublicBranding {
  logo_url?: string | null;
  logo_light_url?: string | null;
  login_page_text?: string | null;
  login_image?: string | null;
  favicon_url?: string | null;
  primary_color?: string | null;
  accent_color?: string | null;
  button_bg_color?: string | null;
  button_text_color?: string | null;
  button_hover_bg_color?: string | null;
  default_theme?: 'light' | 'dark' | null;
}

// Fetch branding without auth (public endpoint resolves from default tenant).
// ?assets=url: images come back as small cacheable URLs instead of inline
// base64 — the inline payload was ~5 MB, which held the logo/image back ~1–2 s.
const fetchPublicBranding = async (): Promise<PublicBranding | null> => {
  try {
    const res = await fetch(`${API_PREFIX}/tenants/branding/?assets=url`, { headers: publicHeaders() });
    if (!res.ok) return null;
    return await res.json() as PublicBranding;
  } catch {
    return null;
  }
};

// Stale-while-revalidate: the last branding seen for this tenant is applied
// synchronously on the next visit, so the logo and hero image appear at once
// and the network fetch only refreshes them.
const BRANDING_CACHE_KEY = () => `ilearn_login_branding_v1_t${localStorage.getItem('tenant_id') || '1'}`;
// Guard against an older backend that ignores ?assets=url and still inlines
// multi-MB data URIs — never stuff that into localStorage.
const BRANDING_CACHE_MAX_CHARS = 50_000;

const readCachedBranding = (): PublicBranding | null => {
  try {
    const raw = localStorage.getItem(BRANDING_CACHE_KEY());
    return raw ? (JSON.parse(raw) as PublicBranding) : null;
  } catch {
    return null;
  }
};

const writeCachedBranding = (b: PublicBranding) => {
  try {
    const raw = JSON.stringify(b);
    if (raw.length <= BRANDING_CACHE_MAX_CHARS) localStorage.setItem(BRANDING_CACHE_KEY(), raw);
  } catch {
    // storage full / disabled — caching is best-effort
  }
};

const LanguageSelector = () => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { locale, setLocale } = useLanguage();
    const { languages } = useInstalledLanguages();

    const handleMouseEnter = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setIsOpen(true);
    };

    const handleMouseLeave = () => {
        closeTimeoutRef.current = setTimeout(() => {
            setIsOpen(false);
        }, 200);
    };

    if (languages.length < 2) return null;

    const items = languages.map((l) => ({
        label: l.name,
        value: l.code,
        onClick: () => { setIsOpen(false); if (l.code !== locale) setLocale(l.code); },
    }));
    const current = languages.find((l) => l.code === locale)?.name ?? locale.toUpperCase();

    return (
        <div
            className={styles.languageSelector}
            ref={triggerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={() => setIsOpen((v) => !v)}
            role="button"
            tabIndex={0}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-label={tr('guest.language', 'Language')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsOpen((v) => !v); } }}
        >
            <Globe size={18} className={styles.langIcon} aria-hidden="true" />
            <span className={`${styles.languagetext} button`}>{current}</span>
            <DropdownMenu
                isOpen={isOpen} onClose={() => setIsOpen(false)}
                items={items} triggerRef={triggerRef}
                position="bottom" align="right"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            />
        </div>
    );
};

export const LoginPage = () => {
    const {
        view,
        globalError, setGlobalError: _setGlobalError,
        showPassword, togglePasswordVisibility,
        forms,
        handlers,
        sso,
        registrationNotice,
        mfaChallenge,
        completeMfa,
        cancelMfa,
        navigate
    } = useLoginPage();

    const [isPasswordFocused, setIsPasswordFocused] = useState(false);
    const [publicLogoUrl, setPublicLogoUrl] = useState<string | null>(null);
    const [publicLogoLightUrl, setPublicLogoLightUrl] = useState<string | null>(null);
    const [loginMessage, setLoginMessage] = useState<string | null>(null);
    const [loginImageUrl, setLoginImageUrl] = useState<string | null>(null);
    const [registrationEnabled, setRegistrationEnabled] = useState(false);
    // True once the public registration settings have been fetched: until then a
    // /login?signup=1 visit shows neither form, so the right one appears without a flash.
    const [registrationChecked, setRegistrationChecked] = useState(false);
    const [registrationFields, setRegistrationFields] = useState<RegistrationField[]>([]);
    const [signupValues, setSignupValues] = useState<Record<string, string>>({});
    const [signupError, setSignupError] = useState('');
    const [signupSubmitting, setSignupSubmitting] = useState(false);
    const [signupSuccess, setSignupSuccess] = useState(false);
    // Sign-up as a wizard (registration form setting): step titles, and which displayed step is open.
    const [signupWizard, setSignupWizard] = useState<{ enabled: boolean; steps: Record<string, string>[] }>({ enabled: false, steps: [] });
    const [signupStepIdx, setSignupStepIdx] = useState(0);
    // Leaving the sign-up view starts it afresh next time (first step, no success message).
    useEffect(() => {
        if (view !== 'signup') { setSignupSuccess(false); setSignupStepIdx(0); }
    }, [view]);
    // Status of the submitted registration (approved / pending_email_confirmation / pending_approval).
    const [signupStatus, setSignupStatus] = useState('');
    // The visitor's own theme (chosen on the guest homepage, or their last
    // in-app theme) wins; only without one does the site default apply.
    const { setTheme } = useTheme();
    const ownTheme = useMemo(() => ownSignedOutTheme(), []);
    const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(ownTheme ?? 'dark');
    const { locale } = useLanguage();
    // Sign-up field label in the visitor's language: the form's own label for
    // this language → the platform's translated label for standard fields →
    // the form's English label → a readable version of the field key.
    const fieldLabel = (field: RegistrationField): string =>
        (field.label_json?.[locale] || '').trim()
        || coreFieldLabel(field.field_key)
        || (field.label_json?.en || '').trim()
        || field.field_key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
    // Country names for the dial-code picker, in the interface language.
    const regionNames = useMemo(() => {
        try { return new Intl.DisplayNames([appLocale()], { type: 'region' }); } catch { return null; }
    }, [locale]);

    // The login screen always renders in the SITE default theme — never the
    // visitor's personal in-app preference. That preference (stored in
    // localStorage) would otherwise bleed a half-applied light theme onto this
    // dark-designed page (light card + dark inputs). Apply a safe default
    // BEFORE first paint so a stale localStorage value can't flash and can't get
    // stuck if the branding fetch fails; the branding effect then refines to the
    // tenant default. We never touch localStorage here, so the user's own theme
    // is restored by ThemeContext right after they log in.
    useLayoutEffect(() => {
        const initial = ownTheme ?? 'dark';
        document.documentElement.setAttribute('data-theme', initial);
        setResolvedTheme(initial);
    }, [ownTheme]);

    useEffect(() => {
        const applyBranding = (b: PublicBranding) => {
            // ── 1. Theme ────────────────────────────────────────────────────
            // The login screen always uses the SITE default theme, never the
            // visitor's personal preference (which would mismatch this
            // dark-designed page). The user's own theme is restored after login.
            const tenantDefault: 'light' | 'dark' =
                b.default_theme === 'light' || b.default_theme === 'dark' ? b.default_theme : 'dark';
            if (!ownSignedOutTheme()) {
                document.documentElement.setAttribute('data-theme', tenantDefault);
                setResolvedTheme(tenantDefault);
            }

            // ── 2. Logos ─────────────────────────────────────────────────
            if (b.logo_url) setPublicLogoUrl(b.logo_url);
            if (b.logo_light_url) setPublicLogoLightUrl(b.logo_light_url);

            // ── 3. Login page text / image ───────────────────────────────
            if (b.login_page_text) setLoginMessage(b.login_page_text);
            // The login image is often a multi-MB upload: ask for a 1600 px rendition.
            if (b.login_image) setLoginImageUrl(sizedImageUrl(b.login_image, 1600));

            // ── 4. Brand colours ─────────────────────────────────────────
            const root = document.documentElement;
            const set = (v: string, val: string | null | undefined) => {
                if (val) root.style.setProperty(v, val);
            };
            set('--purple-dark-1', b.primary_color);
            set('--dm-purple-dark-1', b.primary_color);
            set('--purple-1', b.primary_color);

            // Button colour overrides (if tenant configured them)
            if (b.button_bg_color) {
                set('--button-bg-color', b.button_bg_color);
                set('--purple-1', b.button_bg_color);
                set('--purple-dark-1', b.button_bg_color);
                set('--dm-purple-dark-1', b.button_bg_color);
            }
            if (b.button_text_color) set('--button-text-color', b.button_text_color);
            if (b.button_hover_bg_color) set('--dm-purple-1-on-hover', b.button_hover_bg_color);
            applyTenantAccent(b.accent_color);

            // ── 5. Favicon ────────────────────────────────────────────────
            if (b.favicon_url) {
                document.querySelectorAll('link[rel*="icon"]').forEach(el => el.remove());
                const link = document.createElement('link');
                link.rel = 'icon';
                link.href = b.favicon_url;
                document.head.appendChild(link);
            }
        };

        // Instant paint from the last-seen branding, then refresh from the API.
        const cached = readCachedBranding();
        if (cached) applyBranding(cached);
        // Guard: if the person already signed in and left this page, a late
        // response must not repaint the app in the login screen's theme.
        let alive = true;
        fetchPublicBranding().then(b => {
            if (!b) return;
            writeCachedBranding(b);
            if (alive) applyBranding(b);
        });

        // Check if public registration is enabled and load dynamic fields
        fetch(`${API_PREFIX}/public/registration/form`, { headers: publicHeaders() })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (data?.is_enabled) {
                    setRegistrationEnabled(true);
                    if (data.wizard_enabled) setSignupWizard({ enabled: true, steps: Array.isArray(data.wizard_steps) ? data.wizard_steps : [] });
                    if (data.fields?.length) {
                        const visibleFields = data.fields
                            .filter((f: RegistrationField) => f.is_visible)
                            // Every account needs an e-mail address, whatever an older form says.
                            .map((f: RegistrationField) => (f.field_key === 'email' ? { ...f, is_required: true } : f))
                            .sort((a: RegistrationField, b: RegistrationField) => (a.sort_order || 0) - (b.sort_order || 0));
                        setRegistrationFields(visibleFields);
                        // Pre-fill empty values
                        const initial: Record<string, string> = {};
                        visibleFields.forEach((f: RegistrationField) => { initial[f.field_key] = f.default_value || ''; });
                        // Always add password
                        initial['password'] = '';
                        setSignupValues(initial);
                    }
                }
            })
            .catch(() => {})
            .finally(() => setRegistrationChecked(true));
        return () => { alive = false; };
    }, []);
    // Sign-up requested but registration is off → the login form (UAT DEF-AU-007).
    const showLoginView = view === 'login' || (view === 'signup' && registrationChecked && !registrationEnabled);

    const getEyeIcon = (isActive: boolean) => {
        if (showPassword) {
            return isActive ? eyeOpenWhite : eyeOpenGrey;
        }
        return isActive ? eyeClosedWhite : eyeClosedGrey;
    };

    // Same as the toggle everywhere else: remembered (as the visitor's choice)
    // and carried into the account after sign-in.
    const toggleTheme = () => {
        const next = resolvedTheme === 'light' ? 'dark' : 'light';
        setTheme(next);
        setResolvedTheme(next);
    };

    // ── Sign-up wizard: the steps that have something to fill in, shown as 1 · 2 · 3 ──
    const hasPasswordField = registrationFields.some(f => f.field_type === 'password');
    const wizardStepCount = Math.max(1, signupWizard.steps.length);
    const signupStepOf = (f: RegistrationField) => Math.min(Math.max(f.step || 1, 1), wizardStepCount);
    const wizardStepNos = (() => {
        if (!signupWizard.enabled) return [] as number[];
        const nos = new Set(registrationFields.map(signupStepOf));
        if (!hasPasswordField) nos.add(1); // the password is asked on the first step when the form has no field for it
        return [...nos].sort((a, b) => a - b);
    })();
    const isWizard = wizardStepNos.length > 1;
    const wizardIdx = Math.min(signupStepIdx, Math.max(0, wizardStepNos.length - 1));
    const isLastSignupStep = !isWizard || wizardIdx >= wizardStepNos.length - 1;
    const wizardTitle = (n: number) => {
        const d = signupWizard.steps[n - 1];
        return (d && (d[locale] || d.en)) || tr('login.wizard_step_n', 'Step {n}', { n });
    };
    const signupFieldsShown = isWizard ? registrationFields.filter(f => signupStepOf(f) === wizardStepNos[wizardIdx]) : registrationFields;
    const passwordOnThisStep = !isWizard || wizardIdx === 0;

    const brandLogo =
        resolvedTheme === 'light'
            ? (publicLogoLightUrl || publicLogoUrl || logoIconLight)
            : (publicLogoUrl || logoIconDark);
    // The left panel: the tenant's login picture (if any) under a dark brand wash, the headline, three
    // reasons and a quote — static, no catalogue request (it used to load the whole public catalogue).
    const heroImage = loginImageUrl;
    const heroLogo = publicLogoUrl || logoIconDark;
    const heroPoints = [
        { Icon: BookOpen, title: tr('login.point1_title', 'Relevant learning'), text: tr('login.point1_text', 'Build the skills your teams need for today and tomorrow.') },
        { Icon: BarChart3, title: tr('login.point2_title', 'Measurable impact'), text: tr('login.point2_text', 'Track progress and drive real results.') },
        { Icon: Users, title: tr('login.point3_title', 'A more capable organisation'), text: tr('login.point3_text', 'Empower your people at scale.') },
    ];
    const ssoLabel = (label: string) => (/^(continue|sign in|log in)\b/i.test(label) ? label : tr('login.continue_with', 'Continue with {name}', { name: label }));
    const isMicrosoft = (label: string) => /azure|microsoft|entra|office ?365/i.test(label);

    return (
        <div className={`${styles.loginPage} loginPageRoot`}>
            <div className={styles.loginContainer}>

                {/* ──────────────── LEFT: hero ──────────────── */}
                <div
                    className={`${styles.heroSection} ${heroImage ? styles.heroImage : ''}`}
                    style={heroImage ? { '--hero-photo': `url(${heroImage})` } as React.CSSProperties : undefined}
                >
                    <Link to="/" className={styles.heroLogoLink} aria-label={tr("ui.home", "Home")}>
                        <img src={heroLogo} alt="iLearn" className={styles.heroLogo} />
                    </Link>
                    <div className={styles.heroContent}>
                        <h2 className={styles.heroTitle}>{tr('login.hero_title', 'Develop people. Drive progress.')}</h2>
                        {loginMessage ? (
                            <div className={styles.heroSub} dangerouslySetInnerHTML={{ __html: sanitizeHtml(parseMultilang(loginMessage, locale)) }} />
                        ) : (
                            <p className={styles.heroSub}>{tr('login.hero_sub', 'A modern learning platform for high-performing organisations.')}</p>
                        )}
                        <ul className={styles.heroPoints}>
                            {heroPoints.map(({ Icon, title, text }) => (
                                <li key={title} className={styles.heroPoint}>
                                    <span className={styles.heroPointIcon}><Icon size={24} aria-hidden="true" /></span>
                                    <span>
                                        <span className={styles.heroPointTitle}>{title}</span>
                                        <span className={styles.heroPointText}>{text}</span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <figure className={styles.heroQuote}>
                        <Quote size={34} className={styles.heroQuoteMark} aria-hidden="true" />
                        <blockquote>{tr('login.hero_quote', 'Continuous learning helps our people stay ahead in a rapidly changing world.')}</blockquote>
                    </figure>
                    <div className={styles.glass} aria-hidden="true">
                        <span className={`${styles.glassTile} ${styles.glass1}`}><BarChart3 size={44} /></span>
                        <span className={`${styles.glassTile} ${styles.glass2}`}><Users size={44} /></span>
                        <span className={`${styles.glassTile} ${styles.glass3}`}><Target size={44} /></span>
                        <span className={`${styles.glassTile} ${styles.glass4}`}><GraduationCap size={40} /></span>
                    </div>
                </div>

                {/* ──────────────── RIGHT: panel ──────────────── */}
                <div className={styles.loginPanel}>
                    <div className={styles.logoSection}>
                        <div className={styles.panelActions}>
                            <LanguageSelector />
                            <button
                                type="button"
                                className={styles.themeToggle}
                                onClick={toggleTheme}
                                aria-label={tr("ui.toggle_light_or_dark_theme", "Toggle light or dark theme")}
                            >
                                {resolvedTheme === 'dark' ? <Sun size={18} aria-hidden="true" /> : <Moon size={18} aria-hidden="true" />}
                            </button>
                        </div>
                    </div>
                    <div className={styles.card}>
                    <Link to="/" className={styles.logoLink} aria-label={tr("ui.home", "Home")}>
                        <img src={brandLogo} alt="iLearn" className={styles.logo} />
                    </Link>
                    <div className={styles.panelBody}>

                {mfaChallenge && (
                    <div className={styles.formWrapper}>
                        <MfaChallenge
                            mfaToken={mfaChallenge.mfaToken}
                            allowed={mfaChallenge.allowed}
                            enrolled={mfaChallenge.enrolled}
                            mustEnrol={mfaChallenge.mustEnrol}
                            emailHint={mfaChallenge.emailHint}
                            onComplete={completeMfa}
                            onCancel={cancelMfa}
                        />
                    </div>
                )}

                {view === 'signup' && registrationEnabled && !mfaChallenge && (
                    <>
                        <h1 className={`${styles.title} dm-h1`}>{!signupSuccess
                            ? tr("login.signup_title", "Create your account")
                            : signupStatus === 'approved'
                                ? tr("login.signup_done_title", "Your account is ready")
                                : tr("ui.registration_submitted", "Registration Submitted!")}</h1>

                        <div className={styles.formWrapper}>
                            {signupSuccess ? (
                                <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                                    <p style={{ color: 'var(--status-success-fg)', fontSize: '1rem', marginBottom: '1rem' }}>
                                        {signupStatus === 'approved'
                                            ? tr("login.signup_done_desc", "Your account has been created. You can sign in now.")
                                            : signupStatus === 'pending_email_confirmation'
                                                ? tr("login.signup_confirm_email_desc", "We've sent you an e-mail. Open the link in it to confirm your address.")
                                                : signupStatus === 'pending_approval'
                                                    ? tr("login.signup_pending_desc", "Your registration is waiting for approval. We'll e-mail you once it has been reviewed.")
                                                    : tr("ui.your_registration_has_been_submitted_successfull_e33c09", "Your registration has been submitted successfully.")}
                                    </p>
                                    {!['approved', 'pending_email_confirmation', 'pending_approval'].includes(signupStatus) && (
                                        <p style={{ color: '#8b8aa0', fontSize: '0.875rem' }}>{tr("ui.please_check_your_email_for_further_instructions", "Please check your email for further instructions.")}</p>
                                    )}
                                    <button
                                        type="button"
                                        className={`${styles.submitBtn}`}
                                        style={{ marginTop: '1.5rem' }}
                                        onClick={handlers.handleBackToLogin}
                                    >
                                        {signupStatus === 'approved' ? tr("login.btn_login", "Log in") : tr("ui.back_to_login", "Back to Login")}
                                    </button>
                                </div>
                            ) : (
                            <form onSubmit={async (e) => {
                                e.preventDefault();
                                setSignupError('');
                                // Wizard: "Next" checks this step's answers and opens the next step.
                                if (isWizard && !isLastSignupStep) {
                                    const missing = signupFieldsShown.find(f => f.is_required && !signupValues[f.field_key]?.trim());
                                    if (missing) {
                                        setSignupError(tr("ui.x_is_required", "{v0} is required", { v0: fieldLabel(missing) }));
                                        return;
                                    }
                                    // The step that asks for the password (the form's own password field, or the built-in one
                                    // on the first step) checks it here, not only at "Create account".
                                    const passwordAskedHere = hasPasswordField
                                        ? signupFieldsShown.some(f => f.field_type === 'password')
                                        : passwordOnThisStep;
                                    if (passwordAskedHere && (!signupValues.password || signupValues.password.length < 8)) {
                                        setSignupError(tr("ui.password_must_be_at_least_8_characters", "Password must be at least 8 characters"));
                                        return;
                                    }
                                    // An e-mail or username on this step must still be free, and a password on this step must
                                    // meet the tenant's policy, before the next step opens.
                                    const toCheck: Record<string, string> = {};
                                    for (const f of signupFieldsShown) {
                                        const v = signupValues[f.field_key]?.trim();
                                        if (v && (f.field_key === 'email' || f.field_key === 'username')) toCheck[f.field_key] = v;
                                    }
                                    if (passwordAskedHere) toCheck.password = signupValues.password;
                                    if (Object.keys(toCheck).length) {
                                        setSignupSubmitting(true);
                                        try {
                                            const resp = await fetch(`${API_PREFIX}/public/registration/check`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json', ...publicHeaders() },
                                                body: JSON.stringify(toCheck),
                                            });
                                            const errors: Record<string, string> = resp.ok ? (await resp.json().catch(() => ({})))?.errors ?? {} : {};
                                            const problem = errors.email || errors.username || errors.password;
                                            if (problem) {
                                                setSignupError(localizeAuthMessage(problem) || problem);
                                                return;
                                            }
                                        } catch {
                                            // No answer: "Create account" still checks both.
                                        } finally {
                                            setSignupSubmitting(false);
                                        }
                                    }
                                    setSignupStepIdx(wizardIdx + 1);
                                    return;
                                }
                                // Validate required fields (in a wizard, a missing answer opens its step)
                                for (const f of registrationFields) {
                                    if (f.is_required && !signupValues[f.field_key]?.trim()) {
                                        if (isWizard) setSignupStepIdx(Math.max(0, wizardStepNos.indexOf(signupStepOf(f))));
                                        setSignupError(tr("ui.x_is_required", "{v0} is required", { v0: fieldLabel(f) }));
                                        return;
                                    }
                                }
                                if (!signupValues.password || signupValues.password.length < 8) {
                                    if (isWizard) setSignupStepIdx(hasPasswordField ? Math.max(0, wizardStepNos.indexOf(signupStepOf(registrationFields.find(f => f.field_type === 'password')!))) : 0);
                                    setSignupError(tr("ui.password_must_be_at_least_8_characters", "Password must be at least 8 characters"));
                                    return;
                                }
                                setSignupSubmitting(true);
                                try {
                                    // Combine country code with mobile
                                    const submitValues = { ...signupValues };
                                    if (submitValues.mobile && submitValues._country_code) {
                                        submitValues.mobile = `${submitValues._country_code}${submitValues.mobile.replace(/^0+/, '')}`;
                                    }
                                    delete submitValues._country_code;
                                    const resp = await fetch(`${API_PREFIX}/public/registration/submit`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', ...publicHeaders() },
                                        body: JSON.stringify({ values: submitValues, language: locale }),
                                    });
                                    if (!resp.ok) {
                                        const err = await resp.json().catch(() => ({}));
                                        const detail = String(err.detail || err.message || '');
                                        // A password the policy refuses: back to the step that asks for it.
                                        if (isWizard && detail.startsWith('Password does not meet the policy')) {
                                            const pwField = registrationFields.find(f => f.field_type === 'password');
                                            setSignupStepIdx(pwField ? Math.max(0, wizardStepNos.indexOf(signupStepOf(pwField))) : 0);
                                        }
                                        throw new Error(localizeAuthMessage(detail) || tr("ui.registration_failed_1c671b", "Registration failed"));
                                    }
                                    const done = await resp.json().catch(() => ({}));
                                    setSignupStatus(typeof done?.status === 'string' ? done.status : '');
                                    setSignupSuccess(true);
                                } catch (err: any) {
                                    setSignupError(err.message || tr("ui.registration_failed_1c671b", "Registration failed"));
                                } finally {
                                    setSignupSubmitting(false);
                                }
                            }} className={styles.form}>
                                {isWizard && (
                                    <ol className={styles.wizardSteps} aria-label={tr('login.wizard_steps', 'Registration steps')}>
                                        {wizardStepNos.map((n, i) => (
                                            <li
                                                key={n}
                                                className={`${styles.wizardStep} ${i === wizardIdx ? styles.wizardStepCurrent : ''} ${i < wizardIdx ? styles.wizardStepDone : ''}`}
                                                aria-current={i === wizardIdx ? 'step' : undefined}
                                            >
                                                <button
                                                    type="button"
                                                    className={styles.wizardStepBtn}
                                                    disabled={i >= wizardIdx}
                                                    onClick={() => { setSignupError(''); setSignupStepIdx(i); }}
                                                >
                                                    <span className={styles.wizardStepNo}>{i < wizardIdx ? <Check size={14} aria-hidden="true" /> : i + 1}</span>
                                                    <span className={styles.wizardStepLabel}>{wizardTitle(n)}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ol>
                                )}
                                {isWizard && (
                                    <p className={styles.wizardStepOf}>{tr('login.wizard_step_of', 'Step {n} of {total}', { n: wizardIdx + 1, total: wizardStepNos.length })}</p>
                                )}

                                {signupError && (
                                    <div className={styles.errorBox}>
                                        {signupError}
                                    </div>
                                )}

                                {/* Dynamic fields from API (one step at a time in a wizard) */}
                                {signupFieldsShown.map(field => {
                                    const label = fieldLabel(field);
                                    const placeholder = (field.placeholder_json?.[locale] || '').trim() || label;

                                    if (field.field_type === 'checkbox') {
                                        return (
                                            <div key={field.id} className={styles.inputGroup}>
                                                <label className={styles.checkboxRow}>
                                                    <Checkbox
                                                        checked={signupValues[field.field_key] === 'true'}
                                                        onChange={(e: any) => setSignupValues(prev => ({ ...prev, [field.field_key]: e.target.checked ? 'true' : 'false' }))}
                                                    />
                                                    <span className={styles.label}>{label}{field.is_required && <span className={styles.required}>*</span>}</span>
                                                </label>
                                            </div>
                                        );
                                    }

                                    if (field.field_type === 'textarea') {
                                        return (
                                            <div key={field.id} className={styles.inputGroup}>
                                                <label className={styles.label}>{label}{field.is_required && <span className={styles.required}>*</span>}</label>
                                                <textarea
                                                    className={styles.darkInput}
                                                    placeholder={placeholder}
                                                    rows={3}
                                                    value={signupValues[field.field_key] || ''}
                                                    onChange={e => setSignupValues(prev => ({ ...prev, [field.field_key]: e.target.value }))}
                                                />
                                            </div>
                                        );
                                    }

                                    if (field.field_type === 'dropdown' || field.field_type === 'multi_select') {
                                        const rawOpts = Array.isArray(field.options_json) ? field.options_json : [];
                                        const options = rawOpts.map((o: any) => typeof o === 'string' ? { value: o, label: o } : { value: o?.value || '', label: o?.label || '' });
                                        return (
                                            <div key={field.id} className={styles.inputGroup}>
                                                <label className={styles.label}>{label}{field.is_required && <span className={styles.required}>*</span>}</label>
                                                <select
                                                    className={styles.darkInput}
                                                    value={signupValues[field.field_key] || ''}
                                                    onChange={e => setSignupValues(prev => ({ ...prev, [field.field_key]: e.target.value }))}
                                                >
                                                    <option value="">{tr("ui.select", "Select")} {label}...</option>
                                                    {options.map((o: any) => {
                                                        // Multilang option labels ({en}…{en}{ar}…{ar}) resolve to the
                                                        // interface language; plain standard options get the platform's.
                                                        const parsed = parseMultilang(o.label, locale);
                                                        const text = parsed === o.label ? (coreOptionLabel(field.field_key, o.value) || parsed) : parsed;
                                                        return <option key={o.value} value={o.value}>{text}</option>;
                                                    })}
                                                </select>
                                            </div>
                                        );
                                    }

                                    if (field.field_type === 'file_upload') {
                                        return (
                                            <div key={field.id} className={styles.inputGroup}>
                                                <label className={styles.label}>{label}{field.is_required && <span className={styles.required}>*</span>}</label>
                                                <div className={styles.fileUploadField}>
                                                    <div className={styles.fileUploadIcon}>
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                                                        </svg>
                                                    </div>
                                                    <div className={styles.fileUploadText}>
                                                        {signupValues[field.field_key] ? (
                                                            <div className={styles.fileUploadFileName}>{signupValues[field.field_key]}</div>
                                                        ) : (
                                                            <>
                                                                <div className={styles.fileUploadLabel}>{tr("ui.click_to_upload", "Click to upload")}</div>
                                                                <div className={styles.fileUploadHint}>{tr("ui.pdf_doc_or_image_files", "PDF, DOC, or image files")}</div>
                                                            </>
                                                        )}
                                                    </div>
                                                    <input
                                                        type="file"
                                                        className={styles.fileUploadInput}
                                                        onChange={e => {
                                                            const file = e.target.files?.[0];
                                                            if (file) setSignupValues(prev => ({ ...prev, [field.field_key]: file.name }));
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    }

                                    if (field.field_type === 'password') {
                                        return (
                                            <div key={field.id} className={styles.inputGroup}>
                                                <label className={styles.label}>{label}{field.is_required && <span className={styles.required}>*</span>}</label>
                                                <div className={styles.passwordWrapper}>
                                                    <Input
                                                        type={showPassword ? "text" : "password"}
                                                        placeholder={placeholder}
                                                        autoComplete="new-password"
                                                        className={styles.darkInput}
                                                        variant='dark'
                                                        value={signupValues[field.field_key] || ''}
                                                        onChange={(e: any) => setSignupValues(prev => ({ ...prev, [field.field_key]: e.target.value }))}
                                                        onFocus={() => setIsPasswordFocused(true)}
                                                        onBlur={() => setIsPasswordFocused(false)}
                                                    />
                                                    <button
                                                        type="button"
                                                        className={styles.togglePassword}
                                                        onClick={togglePasswordVisibility}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                    >
                                                        <img
                                                            src={getEyeIcon(isPasswordFocused || !!signupValues[field.field_key])}
                                                            alt={tr("ui.toggle_password", "Toggle password")}
                                                            className={styles.eyeIcon}
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    }
                                    if (field.field_key === 'mobile') {
                                        return (
                                            <div key={field.id} className={styles.inputGroup}>
                                                <label className={styles.label}>{label}{field.is_required && <span className={styles.required}>*</span>}</label>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <select
                                                        className={styles.darkInput}
                                                        style={{ width: '150px', flexShrink: 0, fontSize: '14px' }}
                                                        value={signupValues._country_code || '+971'}
                                                        onChange={e => setSignupValues(prev => ({ ...prev, _country_code: e.target.value }))}
                                                        aria-label={tr("login.signup_dial_code", "Country code")}
                                                    >
                                                        {DIAL_CODES.map(c => (
                                                            // The code is wrapped in a left-to-right isolate so "+971"
                                                            // stays first and intact inside Arabic text.
                                                            <option key={c.code} value={c.code}>{`⁦${c.code}⁩ ${regionNames?.of(c.region) || c.region}`}</option>
                                                        ))}
                                                    </select>
                                                    <input
                                                        type="tel"
                                                        className={styles.darkInput}
                                                        style={{ flex: 1 }}
                                                        placeholder="50 123 4567"
                                                        value={signupValues[field.field_key] || ''}
                                                        onChange={e => setSignupValues(prev => ({ ...prev, [field.field_key]: e.target.value }))}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    }

                                    if (field.field_type === 'date') {
                                        return (
                                            <div key={field.id} className={styles.inputGroup}>
                                                <label className={styles.label}>{label}{field.is_required && <span className={styles.required}>*</span>}</label>
                                                <input
                                                    type="date"
                                                    className={styles.darkInput}
                                                    value={signupValues[field.field_key] || ''}
                                                    onChange={e => setSignupValues(prev => ({ ...prev, [field.field_key]: e.target.value }))}
                                                    style={{ colorScheme: resolvedTheme }}
                                                />
                                            </div>
                                        );
                                    }

                                    {/* Default: text/number/email input — only use type="email" for the email key */}
                                    return (
                                        <div key={field.id} className={styles.inputGroup}>
                                            <label className={styles.label}>{label}{field.is_required && <span className={styles.required}>*</span>}</label>
                                            <Input
                                                type={field.field_key === 'email' ? 'email' : field.field_type === 'number' ? 'number' : 'text'}
                                                placeholder={placeholder}
                                                className={styles.darkInput}
                                                variant='dark'
                                                value={signupValues[field.field_key] || ''}
                                                onChange={(e: any) => setSignupValues(prev => ({ ...prev, [field.field_key]: e.target.value }))}
                                            />
                                        </div>
                                    );
                                })}

                                {/* Password fallback — only shown if admin didn't add a password field (first wizard step) */}
                                {!hasPasswordField && passwordOnThisStep && (
                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>{tr("login.field_password", "Password")}<span className={styles.required}>*</span></label>
                                    <div className={styles.passwordWrapper}>
                                        <Input
                                            type={showPassword ? "text" : "password"}
                                            placeholder={tr("login.field_password", "Password")}
                                            autoComplete="new-password"
                                            className={styles.darkInput}
                                            variant='dark'
                                            value={signupValues.password || ''}
                                            onChange={(e: any) => setSignupValues(prev => ({ ...prev, password: e.target.value }))}
                                            onFocus={() => setIsPasswordFocused(true)}
                                            onBlur={() => setIsPasswordFocused(false)}
                                        />
                                        <button
                                            type="button"
                                            className={styles.togglePassword}
                                            onClick={togglePasswordVisibility}
                                            onMouseDown={(e) => e.preventDefault()}
                                        >
                                            <img
                                                src={getEyeIcon(isPasswordFocused || !!signupValues.password)}
                                                alt={tr("ui.toggle_password", "Toggle password")}
                                                className={styles.eyeIcon}
                                            />
                                        </button>
                                    </div>
                                </div>
                                )}

                                <div className={isWizard ? styles.wizardActions : undefined}>
                                    {isWizard && wizardIdx > 0 && (
                                        <button type="button" className={styles.wizardBack} onClick={() => { setSignupError(''); setSignupStepIdx(wizardIdx - 1); }}>
                                            {tr('login.wizard_back', 'Back')}
                                        </button>
                                    )}
                                    <Button type="submit" className={`${styles.submitBtn} ${styles.signupSubmitBtn}`} disabled={signupSubmitting}>
                                        {!isLastSignupStep
                                            ? tr('login.wizard_next', 'Next')
                                            : signupSubmitting ? tr("login.btn_creating", "Creating...") : tr("login.btn_create_account", "Create account")}
                                    </Button>
                                </div>
                            </form>
                            )}

                            {!signupSuccess && (
                            <>
                            <p className={styles.legalText}>
                                {tr("login.terms_text", "By continuing, you agree to our")} <Link to="/legal/terms" target="_blank" rel="noopener noreferrer" className={styles.legalLink}>{tr("login.terms_link", "Terms")}</Link> {tr("login.terms_and", "and")} <Link to="/legal/privacy" target="_blank" rel="noopener noreferrer" className={styles.legalLink}>{tr("login.privacy_link", "Privacy Policy")}</Link>
                            </p>

                            <div className={styles.bottomLoginWrapper} onClick={handlers.handleBackToLogin}>
                                <span className={`${styles.alreadyHaveAccountText} subtitle-2`}>{tr("login.already_have_account", "Already have an account?")} </span>
                                <a className={`${styles.loginLink} subtitle-1`}>
                                    {tr("login.btn_login", "Log in")}
                                </a>
                            </div>
                            </>
                            )}
                        </div>
                    </>
                )}

                {view === 'reset-success' && (
                    <>
                        <h1 className={`${styles.title} dm-h1`}>{tr("login.reset_success_title", "Password updated!")}</h1>
                        <p className={`${styles.subtitle} body1`}>
                            {tr("login.reset_success_desc", "Your password has been changed successfully. Use your new password to log in.")}
                        </p>
                        <div className={styles.formWrapper}>
                            <Button type="button" className={styles.submitBtn} onClick={handlers.handleBackToLogin}>
                                {tr("login.btn_login", "Log in")}
                            </Button>
                        </div>
                    </>
                )}

                {view === 'reset-password' && (
                    <>
                        <h1 className={`${styles.title} ${styles.mb32} dm-h1`}>{tr("login.reset_title", "Create new password")}</h1>

                        <div className={styles.formWrapper}>
                            <form onSubmit={handlers.onNewPasswordSubmit} className={styles.forgotPasswordForm}>
                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>{tr("login.field_password", "Password")}<span className={styles.required}>*</span></label>
                                    <div className={styles.passwordWrapper}>
                                        <Input
                                            type={showPassword ? "text" : "password"}
                                            placeholder={tr("login.field_password", "Password")}
                                            autoComplete="new-password"
                                            className={`${styles.darkInput} ${forms.newPassword.formState.errors.password ? styles.errorInput : ''}`}
                                            variant='dark'
                                            {...forms.newPassword.register('password')}
                                            onFocus={() => setIsPasswordFocused(true)}
                                            onBlur={(e) => {
                                                forms.newPassword.register('password').onBlur(e);
                                                setIsPasswordFocused(false);
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className={styles.togglePassword}
                                            onClick={togglePasswordVisibility}
                                            onMouseDown={(e) => e.preventDefault()}
                                        >
                                            <img
                                                src={getEyeIcon(isPasswordFocused || !!forms.newPassword.watch('password'))}
                                                alt={tr("ui.toggle_password", "Toggle password")}
                                                className={styles.eyeIcon}
                                            />
                                        </button>
                                    </div>
                                    {forms.newPassword.formState.errors.password ? (
                                        <p className={`${styles.fieldErrorText} subtitle-2`}>{forms.newPassword.formState.errors.password.message as string}</p>
                                    ) : (
                                        <p className={`${styles.passwordHelperText} subtitle-3`}>
                                            {tr("login.password_hint", "Password must be at least 8 characters long and include uppercase, lowercase, a number, and a special character (! @ # $ % ^ & *)")}
                                        </p>
                                    )}
                                </div>

                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>{tr("ui.confirm_password", "Confirm password")}<span className={styles.required}>*</span></label>
                                    <Input
                                        type={showPassword ? "text" : "password"}
                                        placeholder={tr("ui.confirm_password", "Confirm password")}
                                        autoComplete="new-password"
                                        className={`${styles.darkInput} ${forms.newPassword.formState.errors.confirm_password ? styles.errorInput : ''}`}
                                        variant='dark'
                                        {...forms.newPassword.register('confirm_password')}
                                    />
                                    {forms.newPassword.formState.errors.confirm_password && <p className={`${styles.fieldErrorText} subtitle-2`}>{forms.newPassword.formState.errors.confirm_password.message as string}</p>}
                                </div>

                                {/* Server refusals (policy, expired link) were set but never shown here (UAT DEF-AU-003). */}
                                {globalError && (
                                    <p className={`${styles.fieldErrorText} subtitle-2`} style={{textAlign:'center', marginBottom: '0.5rem'}}>
                                        {globalError}
                                    </p>
                                )}
                                <Button type="submit" className={styles.submitBtn} disabled={forms.newPassword.formState.isSubmitting}>
                                    {forms.newPassword.formState.isSubmitting ? tr("login.btn_saving", "Saving...") : tr("login.reset_title", "Create new password")}
                                </Button>
                            </form>

                            <div className={styles.backLinkWrapper}>
                                <button type="button" onClick={handlers.handleBackToLogin} className={styles.backLinkBtn}>
                                    {tr("login.btn_login", "Log in")}
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {view === 'forgot-success' && (
                    <>
                        <h1 className={`${styles.title} dm-h1`}>{tr("login.reset_sent_title", "Check your e-mail")}</h1>
                        <p className={`${styles.subtitle} body1`}>
                            {tr("login.reset_sent_desc", "If that address belongs to an account that signs in with a password, we've sent a link to choose a new one. It works once and expires in 30 minutes. Accounts that sign in through your organisation (SSO) reset their password there.")}
                        </p>
                        <div className={styles.formWrapper}>
                            <Button type="button" className={styles.submitBtn} onClick={() => navigate('#')}>
                                {tr("tags.import.step_done", "Done")}
                            </Button>
                        </div>
                    </>
                )}

                {view === 'forgot' && (
                    <>
                        <h1 className={`${styles.title} dm-h1`}>{tr("login.forgot_title", "Forgot your password?")}</h1>
                        <p className={`${styles.subtitle} body1`}>
                            {tr("login.forgot_desc", "Enter the e-mail address of your account and we'll send you a link to choose a new password.")}
                        </p>

                        <div className={styles.formWrapper}>
                            <form onSubmit={handlers.onForgotSubmit} className={styles.forgotPasswordForm}>
                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>{tr("ui.email_address_b357b5", "Email address")}<span className={styles.required}>*</span></label>
                                    <Input
                                        type="email"
                                        autoComplete="email"
                                        placeholder={tr("login.forgot_email_placeholder", "name@company.com")}
                                        className={`${styles.darkInput} ${forms.forgot.formState.errors.email ? styles.errorInput : ''}`}
                                        variant='dark'
                                        {...forms.forgot.register('email')}
                                    />
                                    {forms.forgot.formState.errors.email && <p className={`${styles.fieldErrorText} subtitle-2`}>{forms.forgot.formState.errors.email.message as string}</p>}
                                </div>

                                {globalError && (
                                    <p className={`${styles.fieldErrorText} subtitle-2`} style={{textAlign:'center', marginBottom: '0.5rem'}}>
                                        {globalError}
                                    </p>
                                )}
                                <Button type="submit" className={styles.submitBtn} disabled={forms.forgot.formState.isSubmitting}>
                                    {forms.forgot.formState.isSubmitting ? tr("login.btn_sending", "Sending...") : tr("login.btn_reset", "Reset password")}
                                </Button>
                            </form>

                            <div className={styles.backLinkWrapper}>
                                <button type="button" onClick={handlers.handleBackToLogin} className={styles.backLinkBtn}>
                                    {tr("login.btn_back", "Back")}
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {view === 'ldap' && sso.selectedLdapProvider && (
                    <>
                        <h1 className={`${styles.title} dm-h1`}>{tr("ui.login_via", "Login via")} {sso.selectedLdapProvider.name}</h1>
                        <p className={`${styles.subtitle} body1`}>{tr("login.subtitle", "Please log in to your account to continue")}</p>

                        <div className={styles.formWrapper}>
                            <Button variant="outlined" className={styles.backButton} onClick={handlers.handleBackToLogin}>
                                {tr("login.btn_back_to_login", "← Back to all login options")}
                            </Button>
                            <form onSubmit={handlers.onLdapSubmit} className={styles.form}>
                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>{tr("login.field_ldap_username", "Username")}<span className={styles.required}>*</span></label>
                                    <Input
                                        type="text"
                                        placeholder={tr("login.field_ldap_username_placeholder", "Your LDAP username")}
                                        disabled={forms.ldap.formState.isSubmitting}
                                        className={`${styles.darkInput} ${forms.ldap.formState.errors.username ? styles.errorInput : ''}`}
                                        variant='dark'
                                        {...forms.ldap.register('username')}
                                    />
                                    {forms.ldap.formState.errors.username && <p className={`${styles.fieldErrorText} subtitle-2`}>{forms.ldap.formState.errors.username.message as string}</p>}
                                </div>
                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>{tr("login.field_password", "Password")}<span className={styles.required}>*</span></label>
                                    <div className={styles.passwordWrapper}>
                                        <Input
                                            type={showPassword ? "text" : "password"}
                                            placeholder={tr("login.field_password", "Password")}
                                            autoComplete="current-password"
                                            disabled={forms.ldap.formState.isSubmitting}
                                            className={`${styles.darkInput} ${forms.ldap.formState.errors.password ? styles.errorInput : ''}`}
                                            variant='dark'
                                            {...forms.ldap.register('password')}
                                            onFocus={() => setIsPasswordFocused(true)}
                                            onBlur={(e) => {
                                                forms.ldap.register('password').onBlur(e);
                                                setIsPasswordFocused(false);
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className={styles.togglePassword}
                                            onClick={togglePasswordVisibility}
                                            onMouseDown={(e) => e.preventDefault()}
                                        >
                                            <img
                                                src={getEyeIcon(isPasswordFocused || !!forms.ldap.watch('password'))}
                                                alt={tr("ui.toggle_password", "Toggle password")}
                                                className={styles.eyeIcon}
                                            />
                                        </button>
                                    </div>
                                    {forms.ldap.formState.errors.password && <p className={`${styles.fieldErrorText} subtitle-2`}>{forms.ldap.formState.errors.password.message as string}</p>}
                                </div>
                                {globalError && (
                                    <p className={`${styles.fieldErrorText} subtitle-2`} style={{textAlign:'center', marginBottom: '0.5rem'}}>
                                        {globalError}
                                    </p>
                                )}
                                <Button type="submit" className={styles.submitBtn} disabled={forms.ldap.formState.isSubmitting}>
                                    {forms.ldap.formState.isSubmitting ? tr("login.btn_logging_in", "Logging in...") : tr("login.btn_login", "Log in")}
                                </Button>
                            </form>
                        </div>
                    </>
                )}

                {showLoginView && !mfaChallenge && (
                    <>
                        <h1 className={`${styles.title} dm-h1`}><Tr k="login.welcome_title" /></h1>
                        <p className={`${styles.subtitle} body1`}><Tr k="login.welcome_subtitle" /></p>

                        <div className={styles.formWrapper}>
                            {registrationNotice && (
                                <div className={`${registrationNotice === 'invalid' ? styles.errorBox : styles.noticeBox} ${styles.noticeSpacing}`} role="status">
                                    {registrationNotice === 'confirmed'
                                        ? tr("login.reg_confirmed", "Your e-mail address is confirmed and your account is ready. You can sign in now.")
                                        : registrationNotice === 'pending'
                                            ? tr("login.reg_confirmed_pending", "Your e-mail address is confirmed. Your registration is now waiting for approval; we'll e-mail you once it has been reviewed.")
                                            : tr("login.reg_link_invalid", "This confirmation link is invalid or has already been used.")}
                                </div>
                            )}
                            <form onSubmit={handlers.onLoginSubmit} className={styles.form}>
                                <div className={styles.inputGroup}>
                                    <label className={styles.label} htmlFor="login-email">{tr("login.field_email", "Username / Email address")}<span className={styles.required}>*</span></label>
                                    <div className={styles.iconField}>
                                        <Mail size={20} className={styles.fieldIcon} aria-hidden="true" />
                                        <Input
                                            id="login-email"
                                            type="text"
                                            placeholder={tr("login.ph_email", "Enter your username or email address")}
                                            autoComplete="username"
                                            disabled={forms.login.formState.isSubmitting}
                                            className={`${styles.darkInput} ${styles.withIcon} ${forms.login.formState.errors.email ? styles.errorInput : ''}`}
                                            variant='dark'
                                            {...forms.login.register('email')}
                                        />
                                    </div>
                                    {forms.login.formState.errors.email && <p className={`${styles.fieldErrorText} subtitle-2`}>{forms.login.formState.errors.email.message as string}</p>}
                                </div>

                                <div className={styles.inputGroup}>
                                    <label className={styles.label} htmlFor="login-password">{tr("login.field_password", "Password")}<span className={styles.required}>*</span></label>
                                    <div className={`${styles.passwordWrapper} ${styles.iconField}`}>
                                        <Lock size={20} className={styles.fieldIcon} aria-hidden="true" />
                                        <Input
                                            id="login-password"
                                            type={showPassword ? "text" : "password"}
                                            placeholder={tr("login.ph_password", "Enter your password")}
                                            autoComplete="current-password"
                                            disabled={forms.login.formState.isSubmitting}
                                            className={`${styles.darkInput} ${styles.withIcon} ${forms.login.formState.errors.password ? styles.errorInput : ''}`}
                                            variant='dark'
                                            {...forms.login.register('password')}
                                            onFocus={() => setIsPasswordFocused(true)}
                                            onBlur={(e) => {
                                                forms.login.register('password').onBlur(e);
                                                setIsPasswordFocused(false);
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className={styles.togglePassword}
                                            onClick={togglePasswordVisibility}
                                            onMouseDown={(e) => e.preventDefault()}
                                        >
                                            <img
                                                src={getEyeIcon(isPasswordFocused || !!forms.login.watch('password'))}
                                                alt={tr("ui.toggle_password", "Toggle password")}
                                                className={styles.eyeIcon}
                                            />
                                        </button>
                                    </div>
                                    {forms.login.formState.errors.password && <p className={`${styles.fieldErrorText} subtitle-2`}>{forms.login.formState.errors.password.message as string}</p>}
                                </div>

                                <div className={styles.formExtras}>
                                    <button
                                        type="button"
                                        className={`${styles.forgotLink} subtitle-1`}
                                        onClick={() => navigate('#forgot')}
                                    >
                                        {tr("login.forgot_title", "Forgot your password?")}
                                    </button>
                                </div>

                                {globalError && (
                                    <p className={`${styles.fieldErrorText} subtitle-2`} style={{textAlign:'center', marginBottom: '0.5rem'}}>
                                        {globalError}
                                    </p>
                                )}
                                <Button type="submit" className={styles.submitBtn} disabled={forms.login.formState.isSubmitting}>
                                    {forms.login.formState.isSubmitting ? tr("login.btn_logging_in", "Logging in...") : (
                                        <span className={styles.btnInner}>{tr("login.btn_login", "Log in")} <ArrowRight size={18} aria-hidden="true" /></span>
                                    )}
                                </Button>
                                {sso.items.length > 0 && (
                                    <>
                                        <div className={styles.separator}>
                                            <span>{tr("ui.or", "OR")}</span>
                                        </div>

                                        {sso.items.length === 1 ? (
                                            <div style={{ width: '100%' }}>
                                                <button
                                                    className={styles.microsoftBtn}
                                                    type="button"
                                                    onClick={sso.items[0].onClick}
                                                >
                                                    <span className={styles.ssoIcon} aria-hidden="true">{isMicrosoft(sso.items[0].label) ? <MicrosoftLogo /> : <KeyRound size={20} />}</span>
                                                    <span className={`${styles.microsoftText} body1`}>
                                                        {ssoLabel(sso.items[0].label)}
                                                    </span>
                                                    <ChevronRight size={20} className={styles.ssoChevron} aria-hidden="true" />
                                                </button>
                                            </div>
                                        ) : (
                                            <div style={{ position: 'relative', width: '100%' }}>
                                                <button
                                                    className={styles.microsoftBtn}
                                                    type="button"
                                                    ref={sso.triggerRef}
                                                    onClick={sso.toggleMenu}
                                                >
                                                    <span className={styles.ssoIcon} aria-hidden="true"><KeyRound size={20} /></span>
                                                    <span className={`${styles.microsoftText} body1`}>{tr("login.sso", "Continue with SSO")}</span>
                                                    <ChevronRight size={20} className={styles.ssoChevron} aria-hidden="true" />
                                                </button>

                                                <DropdownMenu
                                                    isOpen={sso.isOpen}
                                                    onClose={sso.closeMenu}
                                                    items={sso.items}
                                                    triggerRef={sso.triggerRef}
                                                    position="bottom"
                                                    matchTriggerWidth={true}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </form>

                            {registrationEnabled && (
                                <div className={styles.signupSection}>
                                    <p className={styles.signupText}>
                                        <b>{tr("ui.first_time_using_this_site", "First time using this site?")}</b>
                                        <span>{tr("ui.for_full_access_to_this_site_you_first_need_to_c_e7ea16", "For full access to this site, you first need to create an account.")}</span>
                                    </p>
                                    <button
                                        type="button"
                                        className={styles.createAccountBtn}
                                        onClick={() => navigate('#signup')}
                                    >
                                        {tr("login.create_account", "Create account")} <ArrowRight size={17} aria-hidden="true" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}

                    </div>{/* end panelBody */}
                    {/* Panel footer — legal */}
                    <div className={styles.footerLogin}>
                        <Link to="/legal/terms" target="_blank" rel="noopener noreferrer" className={styles.cookiesNotice}>{tr('legal.terms_title', 'Terms of Use')}</Link>
                        <Link to="/legal/privacy" target="_blank" rel="noopener noreferrer" className={styles.cookiesNotice}>{tr('legal.privacy_title', 'Privacy Policy')}</Link>
                        <Link to="/legal/cookies" target="_blank" rel="noopener noreferrer" className={styles.cookiesNotice}>{tr("ui.cookies_notice", "Cookies notice")}</Link>
                    </div>
                    </div>{/* end card */}
                </div>{/* end loginPanel */}
            </div>{/* end loginContainer */}
        </div>
    );
};
/** The four-square Microsoft mark for a Microsoft / Azure sign-in button. */
const MicrosoftLogo = () => (
    <svg width="22" height="22" viewBox="0 0 22 22" focusable="false">
        <rect x="0" y="0" width="10" height="10" fill="#F25022" />
        <rect x="12" y="0" width="10" height="10" fill="#7FBA00" />
        <rect x="0" y="12" width="10" height="10" fill="#00A4EF" />
        <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
);
